import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { randomboxApi } from '../../api/randombox.js';
import { ApiError } from '../../api/client.js';
import type { RbCategory } from '../../api/types.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, Field, SortableTh, SearchInput } from '../../components/ui.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';
import { text, num, dateOnly } from '../../lib/format.js';

/** 원본: randombox/category/randombox_category_list.php */
export function RbCategoriesPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');
  const sort = params.get('sort') ?? 'regdate';
  const dir = (params.get('dir') ?? 'desc') as 'asc' | 'desc';

  const update = useUrlUpdate(params, setParams);
  const [search, setSearch] = useUrlSearch(params, update);
  const debounced = params.get('q') ?? '';

  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; category: RbCategory | null } | null>(
    null,
  );

  const query = useQuery({
    queryKey: ['randombox', 'categories', { page, sort, dir, q: debounced }],
    queryFn: () => randomboxApi.categories({ page, perPage: 50, sort, dir, q: debounced || undefined }),
    placeholderData: keepPreviousData,
  });

  return (
    <section>
      <header className="page-head">
        <h1 className="page-title">랜덤박스 카테고리 관리</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {query.isFetching && <span className="page-head__busy">갱신 중…</span>}
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setFormModal({ mode: 'create', category: null })}
          >
            카테고리 등록
          </button>
        </div>
      </header>

      <div className="filters">
        <Field label="검색 (카테고리명 · 메모)">
          <SearchInput
            value={search}
            placeholder="카테고리명"
            onChange={setSearch}
          />
        </Field>
      </div>

      {query.isPending ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorBox error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableTh label="번호" sortKey="seq" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <SortableTh label="코드" sortKey="companyCode" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <SortableTh label="카테고리명" sortKey="companyName" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <th>메모</th>
                  <SortableTh label="등록일" sortKey="regdate" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((c, i) => (
                  <tr key={c.seq ?? i}>
                    <td className="td-right td-muted">{num(c.seq)}</td>
                    <td><code className="code">{text(c.companyCode)}</code></td>
                    <td>{text(c.companyName)}</td>
                    <td className="td-muted td-clip">{text(c.memo)}</td>
                    <td className="td-muted">{dateOnly(c.regdate)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setFormModal({ mode: 'edit', category: c })}
                      >
                        수정
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            total={query.data.total}
            onChange={(p) => update({ page: p }, false)}
          />
        </>
      )}

      {formModal && (
        <CategoryFormModal
          mode={formModal.mode}
          category={formModal.category}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            void queryClient.invalidateQueries({ queryKey: ['randombox', 'categories'] });
          }}
        />
      )}
    </section>
  );
}

/**
 * 원본: randombox/category/randombox_category_modify.php 의 modifyexe(수정) +
 * 같은 파일의 등록 분기(신규). lottery/CategoriesPage.tsx 의 CategoryFormModal 과
 * 구조가 같다 - 두 모듈을 하나로 합치지 않는 이유도 같다(테이블·타입이 따로라
 * 한쪽만 바뀌어도 다른 쪽이 조용히 깨지는 걸 피하려는 것).
 *
 * lottery 쪽과 다르게 일본어명 · 구분(itemType) 입력칸이 없다 - 이 모듈은
 * 등록/수정에 그 값들이 필요 없다는 요청에 따라 뺐고, 목록 컬럼도 둘 다 뺐다.
 *
 * jpName · itemType 은 그래도 RbCategory 타입·서버 응답에는 남아 있다 - 화면
 * 어디에도 안 보일 뿐, 아래 폼 상태가 그 값을 들고 있다가 저장할 때 그대로
 * 돌려보내야(안 그러면 조용히 지워지거나 기본값으로 되돌아간다) 하기 때문이다.
 */
function CategoryFormModal({
  mode,
  category,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  category: RbCategory | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    companyName: category?.companyName ?? '',
    // 일본어명 · 구분(itemType)은 이 모듈의 등록/수정 화면에 입력칸이 없다.
    // 그래도 값 자체는 폼 상태에 그대로 들고 있다가 저장할 때 같이 보낸다 -
    // 아예 안 보내면 서버 categoryBody 의 기본값('일본어명'='', '구분'='1')으로
    // 떨어져, 저장할 때마다 기존 값을 조용히 지우거나 FANZA 로 되돌리게 된다.
    jpName: category?.jpName ?? '',
    companyCode: category?.companyCode ?? '',
    itemType: category?.itemType ?? '1',
    memo: category?.memo ?? '',
  });

  const save = useMutation({
    mutationFn: () =>
      mode === 'edit'
        ? randomboxApi.updateCategory(category!.seq!, form)
        : randomboxApi.createCategory(form),
    onSuccess: onSaved,
  });

  const valid = form.companyName.trim() !== '' && form.companyCode.trim() !== '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{mode === 'edit' ? '카테고리 수정' : '카테고리 등록'}</h2>

        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="카테고리명 *">
            <input
              className="input"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              required
              maxLength={128}
            />
          </Field>
          <Field label="코드명 *">
            <input
              className="input"
              value={form.companyCode}
              onChange={(e) => setForm((f) => ({ ...f, companyCode: e.target.value }))}
              required
              maxLength={40}
            />
          </Field>
          <Field label="메모" full>
            <input
              className="input"
              value={form.memo}
              onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
              maxLength={2000}
            />
          </Field>

          {/* 코드명은 randombox_product 가 category_code 로 그대로 참조한다 - 수정
              중에 바꾸면 이 코드를 쓰던 랜덤박스들과의 연결이 끊긴다(원본도 같은
              위험을 그대로 안고 있었다. 여기서는 알려주기만 한다). 등록은 원래
              코드가 없으니 해당하지 않는다. */}
          {mode === 'edit' && form.companyCode !== (category?.companyCode ?? '') && (
            <p className="hint hint--warn" style={{ gridColumn: '1 / -1' }}>
              코드명을 바꾸면 이 코드를 쓰던 랜덤박스와의 연결이 끊깁니다.
            </p>
          )}

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : mode === 'edit' ? '저장하기' : '등록하기'}
          </button>
        </form>
      </div>
    </div>
  );
}
